/** A rectangle in pixels. */
export type Rect = { x: number; y: number; w: number; h: number }

/**
 * Squarified treemap (Bruls, Huizing & van Wijk): splits `rect` into one rectangle per item,
 * with area proportional to its value, keeping each as close to a square as possible.
 * Items are placed biggest first, filling rows along the shorter side of the space left.
 */
export function squarify<T>(items: { value: number; item: T }[], rect: Rect): { item: T; rect: Rect }[] {
  const total = items.reduce((s, i) => s + Math.max(i.value, 0), 0)
  if (total <= 0 || rect.w <= 0 || rect.h <= 0) return []

  const scale = (rect.w * rect.h) / total
  const nodes = items
    .filter((i) => i.value > 0)
    .sort((a, b) => b.value - a.value)
    .map((i) => ({ item: i.item, area: i.value * scale }))

  const out: { item: T; rect: Rect }[] = []
  let space = { ...rect }
  let row: typeof nodes = []

  for (let i = 0; i < nodes.length; ) {
    const side = Math.min(space.w, space.h)
    const candidate = [...row, nodes[i]]
    if (row.length === 0 || worstRatio(candidate, side) <= worstRatio(row, side)) {
      row = candidate // adding it keeps the row's shapes at least as square
      i++
    } else {
      space = placeRow(row, space, out) // the row is full: fix it in place, start a new one
      row = []
    }
  }
  if (row.length > 0) placeRow(row, space, out)
  return out
}

/** How far the least-square rectangle in a row is from a square (1 = perfect). */
function worstRatio(row: { area: number }[], side: number): number {
  const sum = row.reduce((s, n) => s + n.area, 0)
  const max = Math.max(...row.map((n) => n.area))
  const min = Math.min(...row.map((n) => n.area))
  return Math.max((side * side * max) / (sum * sum), (sum * sum) / (side * side * min))
}

/** Lays a row along the shorter side of `space`, records it, and returns the space left over. */
function placeRow<T>(row: { item: T; area: number }[], space: Rect, out: { item: T; rect: Rect }[]): Rect {
  const sum = row.reduce((s, n) => s + n.area, 0)
  if (space.w >= space.h) {
    // A column on the left.
    const width = sum / space.h
    let y = space.y
    for (const n of row) {
      const h = n.area / width
      out.push({ item: n.item, rect: { x: space.x, y, w: width, h } })
      y += h
    }
    return { x: space.x + width, y: space.y, w: space.w - width, h: space.h }
  }
  // A row along the top.
  const height = sum / space.w
  let x = space.x
  for (const n of row) {
    const w = n.area / height
    out.push({ item: n.item, rect: { x, y: space.y, w, h: height } })
    x += w
  }
  return { x: space.x, y: space.y + height, w: space.w, h: space.h - height }
}
